import { useEffect, useState } from "react";
import { Box, ButtonBase, Paper, Stack, Typography } from "@mui/material";

import { Loading, Refusal } from "@lopie/kernel-ui";

import Basis from "../components/Basis.jsx";
import { overviewSpecs } from "../lib/spec.js";

/**
 * A count of every type the kernel serves, from the schema and nothing else.
 *
 * Nothing here names a type. A type added to the kernel is a tile on the next
 * load, which is the check that the whole app renders from the kernel and does
 * not keep a second copy of it.
 *
 * Each tile is a question asked of the kernel as the person, so the figure is
 * what *they* can open: two people see different numbers on the same page, and
 * the sentence under the tiles says so.
 */
export default function Overview({ schema, query, onOpen, onAnswer, onCounts }) {
  const [tiles, setTiles] = useState(null);

  useEffect(() => {
    let live = true;
    const specs = overviewSpecs(schema);
    Promise.all(specs.map(async (s) => {
      const out = await query(s.spec);
      return { ...s, out };
    })).then((done) => {
      if (!live) return;
      setTiles(done);
      // What the dashboards screen needs to know a connector has brought something
      // this person can open. A type that could not be counted is not counted.
      onCounts?.(Object.fromEntries(done.filter((t) => t.out.ok)
        .map((t) => [t.type, Number(t.out.result.rows[0]?.count ?? 0)])));
      const first = done.find((t) => t.out.ok);
      if (first) onAnswer?.(first.out.result);
    });
    return () => { live = false; };
  }, [schema, query, onAnswer, onCounts]);

  if (!tiles) return <Loading label="Counting what you can open" minHeight={160} />;

  const failed = tiles.find((t) => !t.out.ok);
  const asked = tiles.find((t) => t.out.ok)?.out.result;

  return (
    <Stack sx={{ gap: 2 }}>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 180px), 1fr))",
        }}
      >
        {tiles.map((t) => (
          <Paper key={t.type} variant="outlined" sx={{ p: 0 }}>
            <ButtonBase
              onClick={() => onOpen(t.type)}
              sx={{ display: "block", width: "100%", textAlign: "left", p: 2, borderRadius: 1 }}
              aria-label={t.out.ok
                ? `${t.type}: ${t.out.result.rows[0]?.count ?? 0} records you can open. Open in the builder.`
                : `${t.type}: could not be counted. Open in the builder.`}
            >
              <Typography variant="body2" color="text.secondary">{t.type}</Typography>
              <Typography
                component="div"
                sx={{ fontSize: "1.75rem", fontWeight: 600, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}
              >
                {t.out.ok ? Number(t.out.result.rows[0]?.count ?? 0).toLocaleString() : "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t.out.ok ? "records you can open" : "could not be counted"}
              </Typography>
            </ButtonBase>
          </Paper>
        ))}
      </Box>
      {failed ? <Refusal failure={failed.out.error} actor="Insights" /> : null}
      {asked ? <Basis asOf={asked.as_of} why={asked.as_of_basis} /> : null}
    </Stack>
  );
}
